const DEFINITION_TITLE = '定義 / 型 / マクロ';
const FUNCTION_TITLE = '関数 / 宣言';
const PRAGMA_TITLE = 'pragma / 制御';

const referenceMetadata = {
  '<assert.h>': {
    summary: '実行時アサーションと静的アサーションを扱う診断用ヘッダです。',
    note: null
  },
  '<complex.h>': {
    summary: '複素数型、複素数マクロ、複素数関数群をまとめたヘッダです。',
    note: '複素数関数は double / float / long double complex の各版を含みます。'
  },
  '<ctype.h>': {
    summary: '文字種の判定と大小文字変換を提供します。',
    note: null
  },
  '<errno.h>': {
    summary: 'ライブラリ関数のエラー状態を表す識別子を定義します。',
    note: 'Annex K の `errno_t` も含めています。'
  },
  '<fenv.h>': {
    summary: '浮動小数点例外、丸めモード、演算環境を制御します。',
    note: null
  },
  '<float.h>': {
    summary: '浮動小数点型の精度・範囲・実装特性を表すマクロ群です。',
    note: null
  },
  '<inttypes.h>': {
    summary: '固定幅整数型の書式指定マクロと最大幅整数向け関数を提供します。',
    note: '`N` は実装が提供するビット幅を表すプレースホルダです。'
  },
  '<iso646.h>': {
    summary: '演算子の代替表記トークンを定義します。',
    note: null
  },
  '<limits.h>': {
    summary: '整数型と文字型の範囲を表す実装定数です。',
    note: null
  },
  '<locale.h>': {
    summary: 'ロケールカテゴリの設定と数値書式情報を扱います。',
    note: null
  },
  '<math.h>': {
    summary: '実数向け数学関数、分類マクロ、誤差処理マクロを提供します。',
    note: '実数関数は通常 double / float / long double の各版を含みます。'
  },
  '<setjmp.h>': {
    summary: '非局所ジャンプによる制御の保存と復帰を扱います。',
    note: null
  },
  '<signal.h>': {
    summary: 'シグナル番号とシグナルハンドラ登録関数を提供します。',
    note: null
  },
  '<stdalign.h>': {
    summary: 'アラインメント指定用マクロを提供します。',
    note: null
  },
  '<stdarg.h>': {
    summary: '可変長引数を処理するためのマクロと型です。',
    note: null
  },
  '<stdatomic.h>': {
    summary: 'アトミック型、メモリ順序、総称アトミック操作を提供します。',
    note: '`A`、`C`、`M`、`key` は規格本文で使うプレースホルダです。'
  },
  '<stdbool.h>': {
    summary: '真偽値用の別名と定数マクロを定義します。',
    note: null
  },
  '<stddef.h>': {
    summary: 'サイズ、オフセット、ワイド文字などの基本定義をまとめています。',
    note: 'Annex K の `rsize_t` も含めています。`sizeof` は関数ではなく演算子で、配列に使うと全体サイズ、ポインタに使うとポインタ自身のサイズになります。'
  },
  '<stdint.h>': {
    summary: '幅が明確な整数型と、その範囲・生成マクロを定義します。',
    note: '`N` は実装が提供するビット幅を表すプレースホルダです。'
  },
  '<stdio.h>': {
    summary: '標準入出力、ファイル入出力、整形入出力を扱います。',
    note: 'Annex K の `_s` 系関数と関連識別子も含めています。危険な `gets` は C11 で削除されており、このサイトでは `fgets` を推奨します。'
  },
  '<stdlib.h>': {
    summary: '変換、乱数、メモリ管理、プロセス終了、探索・整列を扱います。',
    note: 'Annex K の制約ハンドラや `_s` 系関数も含めています。'
  },
  '<stdnoreturn.h>': {
    summary: '戻らない関数を表す `noreturn` を提供します。',
    note: null
  },
  '<string.h>': {
    summary: 'メモリ操作と文字列操作を扱います。',
    note: 'Annex K の bounds-checking interfaces も含めています。'
  },
  '<tgmath.h>': {
    summary: '実数・複素数の型に応じて適切な数学関数を選ぶ総称マクロです。',
    note: 'ここに並ぶ名前は関数ではなく型総称マクロです。'
  },
  '<threads.h>': {
    summary: 'スレッド、ミューテックス、条件変数、スレッド固有記憶域を扱います。',
    note: null
  },
  '<time.h>': {
    summary: '日時、CPU 時間、時刻変換、書式化を扱います。',
    note: 'Annex K の `_s` 系時刻関数も含めています。'
  },
  '<uchar.h>': {
    summary: 'UTF-16 / UTF-32 コード単位と多バイト変換を扱います。',
    note: null
  },
  '<wchar.h>': {
    summary: 'ワイド文字列、ワイド入出力、多バイト変換を扱います。',
    note: 'Annex K の `_s` 系ワイド文字関数も含めています。'
  },
  '<wctype.h>': {
    summary: 'ワイド文字向けの文字分類と文字変換を扱います。',
    note: null
  }
};

const mathVariantBases = new Set([
  'acos', 'asin', 'atan', 'atan2', 'cos', 'sin', 'tan',
  'acosh', 'asinh', 'atanh', 'cosh', 'sinh', 'tanh',
  'exp', 'exp2', 'expm1', 'frexp', 'ilogb', 'ldexp',
  'log', 'log10', 'log1p', 'log2', 'logb', 'modf',
  'scalbn', 'scalbln', 'cbrt', 'fabs', 'hypot', 'pow',
  'sqrt', 'erf', 'erfc', 'lgamma', 'tgamma', 'ceil',
  'floor', 'nearbyint', 'rint', 'lrint', 'llrint',
  'round', 'lround', 'llround', 'trunc', 'fmod',
  'remainder', 'remquo', 'copysign', 'nan', 'nextafter',
  'nexttoward', 'fdim', 'fmax', 'fmin', 'fma'
]);

const complexVariantBases = new Set([
  'cacos', 'casin', 'catan', 'ccos', 'csin', 'ctan',
  'cacosh', 'casinh', 'catanh', 'ccosh', 'csinh', 'ctanh',
  'cexp', 'clog', 'cabs', 'cpow', 'csqrt', 'carg',
  'cimag', 'CMPLX', 'conj', 'cproj', 'creal'
]);

const mathBaseMeanings = {
  acos: '逆余弦を計算します。',
  asin: '逆正弦を計算します。',
  atan: '逆正接を計算します。',
  atan2: '2 変数から象限を考慮した逆正接を計算します。',
  cos: '余弦を計算します。',
  sin: '正弦を計算します。',
  tan: '正接を計算します。',
  acosh: '逆双曲線余弦を計算します。',
  asinh: '逆双曲線正弦を計算します。',
  atanh: '逆双曲線正接を計算します。',
  cosh: '双曲線余弦を計算します。',
  sinh: '双曲線正弦を計算します。',
  tanh: '双曲線正接を計算します。',
  exp: '自然指数関数を計算します。',
  exp2: '2 を底にした指数関数を計算します。',
  expm1: '`exp(x) - 1` を精度よく計算します。',
  frexp: '仮数部と指数部へ分解します。',
  ilogb: '指数部を整数として取り出します。',
  ldexp: '仮数と指数から値を組み立てます。',
  log: '自然対数を計算します。',
  log10: '10 を底にした対数を計算します。',
  log1p: '`log(1 + x)` を精度よく計算します。',
  log2: '2 を底にした対数を計算します。',
  logb: '指数に相当する値を返します。',
  modf: '整数部と小数部へ分解します。',
  scalbn: '2 の n 乗倍を計算します。',
  scalbln: '2 の long 整数乗倍を計算します。',
  cbrt: '立方根を計算します。',
  fabs: '絶対値を返します。',
  hypot: '直交成分から斜辺長を計算します。',
  pow: 'べき乗を計算します。',
  sqrt: '平方根を計算します。',
  erf: '誤差関数を計算します。',
  erfc: '相補誤差関数を計算します。',
  lgamma: 'ガンマ関数の絶対値の対数を返します。',
  tgamma: 'ガンマ関数を計算します。',
  ceil: '天井値を返します。',
  floor: '床値を返します。',
  nearbyint: '現在の丸めモードに従って丸めます。',
  rint: '現在の丸めモードに従って整数値へ丸めます。',
  lrint: '丸めた結果を long で返します。',
  llrint: '丸めた結果を long long で返します。',
  round: '四捨五入した値を返します。',
  lround: '四捨五入した結果を long で返します。',
  llround: '四捨五入した結果を long long で返します。',
  trunc: '小数部を切り捨てます。',
  fmod: '剰余を返します。',
  remainder: '丸め規則に従う剰余を返します。',
  remquo: '剰余と商の一部を同時に返します。',
  copysign: '絶対値と符号を組み合わせた値を返します。',
  nan: 'NaN 値を生成します。',
  nextafter: '表現可能な次の値へ進めます。',
  nexttoward: 'long double 方向へ次の値へ進めます。',
  fdim: '正の差だけを返します。',
  fmax: '大きい方の値を返します。',
  fmin: '小さい方の値を返します。',
  fma: '乗算と加算を一度に計算します。'
};

const complexBaseMeanings = {
  cacos: '複素数の逆余弦を計算します。',
  casin: '複素数の逆正弦を計算します。',
  catan: '複素数の逆正接を計算します。',
  ccos: '複素数の余弦を計算します。',
  csin: '複素数の正弦を計算します。',
  ctan: '複素数の正接を計算します。',
  cacosh: '複素数の逆双曲線余弦を計算します。',
  casinh: '複素数の逆双曲線正弦を計算します。',
  catanh: '複素数の逆双曲線正接を計算します。',
  ccosh: '複素数の双曲線余弦を計算します。',
  csinh: '複素数の双曲線正弦を計算します。',
  ctanh: '複素数の双曲線正接を計算します。',
  cexp: '複素指数関数を計算します。',
  clog: '複素対数を計算します。',
  cabs: '複素数の絶対値を返します。',
  cpow: '複素数のべき乗を計算します。',
  csqrt: '複素平方根を計算します。',
  carg: '偏角を返します。',
  cimag: '虚部を返します。',
  CMPLX: '実部と虚部から複素数値を組み立てます。',
  conj: '共役複素数を返します。',
  cproj: 'リーマン球面への射影値を返します。',
  creal: '実部を返します。'
};

const exactDefinitionDescriptions = {
  NDEBUG: {
    meaning: '`assert` を無効化するためのマクロです。',
    usage: 'デバッグ用チェックを外したいビルドで、`#include <assert.h>` より前に定義して使います。'
  },
  static_assert: {
    meaning: 'コンパイル時に条件を検査するための表記です。',
    usage: '型サイズや前提条件を静的に確認したい場面で使います。'
  },
  NULL: {
    meaning: 'ヌルポインタ定数です。',
    usage: 'ポインタがどこも指していないことを表したいときに代入や比較に使います。'
  },
  FILE: {
    meaning: 'ストリームを表す型です。',
    usage: '`fopen` などで得た値を `FILE *` で保持し、入出力関数へ渡して使います。'
  },
  fpos_t: {
    meaning: 'ファイル位置情報を保持する型です。',
    usage: '`fgetpos` と `fsetpos` の組み合わせで、読み書き位置を保存・復元するときに使います。'
  },
  size_t: {
    meaning: 'サイズや個数を表す符号なし整数型です。',
    usage: '配列長、バイト数、`sizeof` の結果を受ける変数に使います。'
  },
  ptrdiff_t: {
    meaning: 'ポインタ差分を表す符号付き整数型です。',
    usage: '同じ配列内のポインタ同士を引いた結果を安全に受けるときに使います。'
  },
  max_align_t: {
    meaning: '最も厳しいアラインメント要求を表せる型です。',
    usage: '汎用バッファの整列を考えるときの基準として使います。'
  },
  wchar_t: {
    meaning: 'ワイド文字を表す型です。',
    usage: 'ワイド文字列や `wchar.h` の関数を扱う変数宣言に使います。'
  },
  wint_t: {
    meaning: 'ワイド文字関数が文字や特殊値を返すための型です。',
    usage: '`getwc` や `towupper` の戻り値を受けるときに使います。'
  },
  mbstate_t: {
    meaning: '多バイト変換の途中状態を保持する型です。',
    usage: '分割入力をまたぐ UTF 変換や状態依存エンコーディングで使います。'
  },
  jmp_buf: {
    meaning: '`setjmp` が保存する実行コンテキストの型です。',
    usage: '`setjmp` で保存し、`longjmp` で復帰するときのバッファとして宣言します。'
  },
  va_list: {
    meaning: '可変長引数列を走査するための型です。',
    usage: '`va_start` で初期化し、`va_arg` と `va_end` を組み合わせて使います。'
  },
  errno: {
    meaning: '直近のライブラリ呼び出しで起きた失敗理由を保持する識別子です。',
    usage: '失敗を返した関数の直後に参照し、`perror` や条件分岐で原因を調べるときに使います。'
  },
  errno_t: {
    meaning: 'Annex K の安全関数群でエラーコードを表す型です。',
    usage: '`_s` 系関数の戻り値を受け、0 かどうかで成功判定するときに使います。'
  },
  rsize_t: {
    meaning: 'Annex K で境界検査付き API のサイズを表す型です。',
    usage: '`memcpy_s` や `strcpy_s` に渡す最大サイズの表現に使います。'
  },
  'struct lconv': {
    meaning: 'ロケール依存の数値や通貨の書式情報を持つ構造体型です。',
    usage: '`localeconv` の戻り値を通して、小数点記号や桁区切り設定を参照するときに使います。'
  },
  'struct tm': {
    meaning: '年月日や時分秒を分解して保持する構造体型です。',
    usage: '`localtime` や `gmtime` の結果を参照し、日時の各成分を扱うときに使います。'
  },
  'struct timespec': {
    meaning: '秒とナノ秒で時刻を表す構造体型です。',
    usage: '高精度な時刻取得やスレッド待機時間の指定に使います。'
  },
  div_t: {
    meaning: '整数除算の商と余りをまとめて返す構造体型です。',
    usage: '`div` の戻り値を受けて、商と余りの両方を同時に取り出すときに使います。'
  },
  ldiv_t: {
    meaning: 'long 整数除算の商と余りをまとめて返す構造体型です。',
    usage: '`ldiv` の戻り値を受けるために使います。'
  },
  lldiv_t: {
    meaning: 'long long 整数除算の商と余りをまとめて返す構造体型です。',
    usage: '`lldiv` の戻り値を受けるために使います。'
  },
  imaxdiv_t: {
    meaning: '`intmax_t` 除算の商と余りをまとめて返す構造体型です。',
    usage: '`imaxdiv` の戻り値を受けるために使います。'
  },
  fenv_t: {
    meaning: '浮動小数点演算環境を保持する型です。',
    usage: '`fegetenv` や `fesetenv` で環境を保存・復元するときに使います。'
  },
  fexcept_t: {
    meaning: '浮動小数点例外フラグ集合を保持する型です。',
    usage: '`fegetexceptflag` と `fesetexceptflag` の引数に使います。'
  },
  clock_t: {
    meaning: 'プロセッサ時間を表す型です。',
    usage: '`clock` の戻り値を保持し、処理時間の差分計算に使います。'
  },
  time_t: {
    meaning: 'カレンダー時刻を表す型です。',
    usage: '`time` や `mktime` の戻り値を保持し、日時変換に使います。'
  },
  sig_atomic_t: {
    meaning: 'シグナルハンドラから安全に読み書きしやすい整数型です。',
    usage: 'ハンドラと通常コードの間で簡単なフラグを共有するときに使います。'
  },
  bool: {
    meaning: '真偽値を表す別名です。',
    usage: '条件の結果や状態フラグを `true` / `false` で表したいときに使います。'
  },
  true: {
    meaning: '真を表すマクロです。',
    usage: '`bool` 型の変数へ真を代入したいときに使います。'
  },
  false: {
    meaning: '偽を表すマクロです。',
    usage: '`bool` 型の変数へ偽を代入したいときに使います。'
  },
  __bool_true_false_are_defined: {
    meaning: '`stdbool.h` の真偽値マクロが定義済みであることを示す識別子です。',
    usage: '移植コードで `stdbool.h` 相当の定義が利用可能か確認したいときに使います。'
  },
  thread_local: {
    meaning: 'スレッドごとに独立した記憶域を持たせる記憶域指定子です。',
    usage: '各スレッドで別々の状態を持つグローバル変数や静的変数に付けて使います。'
  },
  once_flag: {
    meaning: '`call_once` 用の一度きり初期化フラグ型です。',
    usage: 'グローバル初期化を一度だけ実行したいときに宣言して使います。'
  },
  ONCE_FLAG_INIT: {
    meaning: '`once_flag` を初期化するための定数です。',
    usage: '`once_flag flag = ONCE_FLAG_INIT;` のように静的初期化で使います。'
  },
  atomic_flag: {
    meaning: '最小構成のアトミックフラグ型です。',
    usage: 'スピンロックや一度だけセットしたいフラグを表すときに使います。'
  },
  ATOMIC_FLAG_INIT: {
    meaning: '`atomic_flag` を未セット状態で初期化するための定数です。',
    usage: '`atomic_flag flag = ATOMIC_FLAG_INIT;` のように宣言時に使います。'
  },
  ATOMIC_VAR_INIT: {
    meaning: 'アトミック変数を初期値付きで初期化するためのマクロです。',
    usage: '静的記憶域のアトミックオブジェクトを宣言時に初期化するときに使います。'
  },
  alignas: {
    meaning: '必要なアラインメントを指定するための表記です。',
    usage: 'SIMD 用バッファやハードウェア制約のあるデータに整列条件を付けたいときに使います。'
  },
  __alignas_is_defined: {
    meaning: '`alignas` マクロが利用可能であることを示す識別子です。',
    usage: '移植コードでアラインメント指定マクロの利用可否を判定したいときに使います。'
  },
  noreturn: {
    meaning: 'その関数が呼び出し元へ戻らないことを示す表記です。',
    usage: '`exit` 相当のラッパーや致命的エラー処理関数の宣言に付けて使います。'
  },
  complex: {
    meaning: '複素数型を表すための表記です。',
    usage: '`double complex z;` のように実部と虚部を持つ値を宣言するときに使います。'
  },
  imaginary: {
    meaning: '虚数型を表すための表記です。',
    usage: '純虚数成分だけを明示したい特殊な宣言で使います。'
  },
  _Complex_I: {
    meaning: '複素数の虚数単位を表す定数です。',
    usage: '複素数リテラルを組み立てるときに虚部側へ掛けて使います。'
  },
  _Imaginary_I: {
    meaning: '虚数単位を表す定数です。',
    usage: '虚数型対応環境で虚部の表現に使います。'
  },
  I: {
    meaning: '複素数・虚数の虚数単位を表すマクロです。',
    usage: '`1.0 + 2.0 * I` のように複素数値を組み立てるときに使います。'
  },
  float_t: {
    meaning: '浮動小数点演算結果を効率よく保持するための実装依存型です。',
    usage: '数学関数周辺で評価用の型を抽象化したいときに使います。'
  },
  double_t: {
    meaning: '浮動小数点演算結果を効率よく保持するための実装依存型です。',
    usage: '評価精度に従った中間値型として使います。'
  },
  char16_t: {
    meaning: 'UTF-16 のコード単位を表す型です。',
    usage: 'UTF-16 データの変換先や文字列バッファに使います。'
  },
  char32_t: {
    meaning: 'UTF-32 のコード単位を表す型です。',
    usage: 'UTF-32 データの変換先や文字列バッファに使います。'
  },
  cnd_t: {
    meaning: '条件変数を表す型です。',
    usage: 'スレッド間で待機と通知を行う同期オブジェクトとして使います。'
  },
  thrd_t: {
    meaning: 'スレッド識別子を表す型です。',
    usage: '`thrd_create` の結果を保持し、`thrd_join` や `thrd_detach` に渡して使います。'
  },
  tss_t: {
    meaning: 'スレッド固有記憶域キーを表す型です。',
    usage: '各スレッドに個別データをぶら下げたいときに使います。'
  },
  mtx_t: {
    meaning: 'ミューテックスを表す型です。',
    usage: '共有データの排他制御を行うロックとして使います。'
  },
  tss_dtor_t: {
    meaning: 'スレッド固有記憶域の破棄関数型です。',
    usage: '`tss_create` でキーに後始末関数を関連付けるときに使います。'
  },
  thrd_start_t: {
    meaning: 'スレッド開始関数の型です。',
    usage: '`thrd_create` に渡すエントリ関数のシグネチャとして使います。'
  },
  wctrans_t: {
    meaning: 'ワイド文字変換規則を表す型です。',
    usage: '`wctrans` で取得し、`towctrans` に渡して変換規則を適用するときに使います。'
  },
  wctype_t: {
    meaning: 'ワイド文字分類規則を表す型です。',
    usage: '`wctype` で取得し、`iswctype` に渡して分類判定を行うときに使います。'
  }
};

const exactFunctionDescriptions = {
  assert: {
    meaning: '条件が偽なら診断して異常終了する関数です。',
    usage: '実行時に崩れてはいけない前提条件を `assert(condition);` の形で置いて使います。'
  },
  setlocale: {
    meaning: 'ロケールカテゴリの設定や取得を行う関数です。',
    usage: '数値や日付や文字分類の振る舞いをロケールに合わせたいときに呼び出します。'
  },
  localeconv: {
    meaning: '現在のロケールに対応する書式情報を返す関数です。',
    usage: '小数点や通貨表記を表示側へ反映したいときに使います。'
  },
  setjmp: {
    meaning: '現在の実行位置を保存する関数です。',
    usage: '戻り先を保存しておき、別の場所から `longjmp` で一気に戻りたいときに使います。'
  },
  longjmp: {
    meaning: '`setjmp` で保存した位置へ復帰する関数です。',
    usage: '深い呼び出し階層からエラー脱出したいときに、保存済みの `jmp_buf` を渡して使います。'
  },
  signal: {
    meaning: 'シグナルに対するハンドラを登録する関数です。',
    usage: '`SIGINT` や `SIGTERM` などに対して処理関数を結び付けたいときに使います。'
  },
  raise: {
    meaning: '現在のプロセスへシグナルを送る関数です。',
    usage: 'テストや異常終了処理で、自分自身に標準シグナルを発生させたいときに使います。'
  },
  'va_start': {
    meaning: '可変長引数の走査を開始するマクロです。',
    usage: '固定引数の最後の名前を指定して初期化し、その後 `va_arg` を繰り返して使います。'
  },
  'va_arg': {
    meaning: '次の可変長引数を取り出すマクロです。',
    usage: '期待する型を明示して 1 個ずつ読み出したいときに使います。'
  },
  'va_copy': {
    meaning: '可変長引数列の状態を複製するマクロです。',
    usage: '同じ引数列を 2 回走査したいときに、元の `va_list` から複製して使います。'
  },
  'va_end': {
    meaning: '可変長引数の走査を終了するマクロです。',
    usage: '`va_start` や `va_copy` を使った後の後始末として必ず呼びます。'
  },
  offsetof: {
    meaning: '構造体メンバのオフセットを求めるマクロです。',
    usage: 'バイナリレイアウトや埋め込み構造体の位置を計算したいときに使います。'
  },
  call_once: {
    meaning: '指定した初期化処理を一度だけ実行する関数です。',
    usage: '複数スレッドから競合なく遅延初期化したいときに使います。'
  },
  thrd_current: {
    meaning: '現在のスレッド識別子を返す関数です。',
    usage: '自分自身のスレッド ID を比較や記録に使いたいときに呼び出します。'
  },
  thrd_yield: {
    meaning: '実行権を他スレッドへ譲る関数です。',
    usage: 'ビジー待機ループの圧を下げたいときに挟んで使います。'
  },
  time: {
    meaning: '現在時刻を取得する関数です。',
    usage: '現在のカレンダー時刻を `time_t` として得たいときに使います。'
  },
  clock: {
    meaning: 'プログラムが消費した CPU 時間を返す関数です。',
    usage: '処理時間の粗い計測を行いたいときに開始時と終了時の差分で使います。'
  },
  difftime: {
    meaning: '2 つの時刻差を秒単位の実数で返す関数です。',
    usage: '開始時刻と終了時刻の差を計算するときに使います。'
  },
  mktime: {
    meaning: '`struct tm` を `time_t` に変換する関数です。',
    usage: '分解済みの日時からタイムスタンプを作りたいときに使います。'
  },
  timespec_get: {
    meaning: '`struct timespec` に現在時刻を格納する関数です。',
    usage: '秒とナノ秒を分けて高精度時刻を取得したいときに使います。'
  },
  asctime: {
    meaning: '`struct tm` を固定形式の文字列へ変換する関数です。',
    usage: '簡易的に日時を人間向け文字列へ直したいときに使います。'
  },
  ctime: {
    meaning: '`time_t` を固定形式の文字列へ変換する関数です。',
    usage: 'タイムスタンプをすぐ表示用文字列へ変換したいときに使います。'
  },
  gmtime: {
    meaning: '`time_t` を UTC の分解時刻へ変換する関数です。',
    usage: '時刻を世界標準時ベースで扱いたいときに使います。'
  },
  localtime: {
    meaning: '`time_t` をローカルタイムの分解時刻へ変換する関数です。',
    usage: '現在地のタイムゾーンを前提に日時を表示したいときに使います。'
  },
  strftime: {
    meaning: '日時構造体を書式付き文字列へ変換する関数です。',
    usage: '`%Y-%m-%d` などの書式を指定して表示用文字列を作るときに使います。'
  },
  asctime_s: {
    meaning: '`asctime` の境界検査付き版です。',
    usage: '出力先バッファのサイズを明示し、安全に日時文字列を作りたいときに使います。'
  },
  ctime_s: {
    meaning: '`ctime` の境界検査付き版です。',
    usage: '十分な大きさのバッファを渡して安全に日時文字列を得たいときに使います。'
  },
  gmtime_s: {
    meaning: '`gmtime` の結果を呼び出し側バッファへ書き込む版です。',
    usage: '静的内部バッファに依存せず UTC 分解時刻を得たいときに使います。'
  },
  localtime_s: {
    meaning: '`localtime` の結果を呼び出し側バッファへ書き込む版です。',
    usage: '静的内部バッファに依存せずローカル時刻を得たいときに使います。'
  },
  gets_s: {
    meaning: 'Annex K の境界検査付き文字列入力関数です。',
    usage:
      '対応実装でのみ利用できる関数で、移植性には注意が必要です。行入力を安全に扱いたい一般的な C コードでは `fgets` を優先して考えます。'
  }
};

const normalizeFunctionName = (name, baseSet) => {
  if (name.endsWith('f') && baseSet.has(name.slice(0, -1))) {
    return { baseName: name.slice(0, -1), variant: 'float' };
  }
  if (name.endsWith('l') && baseSet.has(name.slice(0, -1))) {
    return { baseName: name.slice(0, -1), variant: 'long double' };
  }
  return { baseName: name, variant: 'default' };
};

const meaningUsage = (meaning, usage) => ({ meaning, usage });

const inferSectionKind = (section) => {
  if (section.title.includes('pragma')) return 'pragma';
  const hasFunctionLikeItems = section.items.some((item) => item.includes(';'));
  return hasFunctionLikeItems ? 'function' : 'definition';
};

const normalizeSectionTitle = (kind) => {
  if (kind === 'pragma') return PRAGMA_TITLE;
  if (kind === 'function') return FUNCTION_TITLE;
  return DEFINITION_TITLE;
};

const sanitizeToken = (token) =>
  token
    .replace(/[,*]/g, '')
    .replace(/∗/g, '')
    .trim();

const expandDefinitionItems = (item) => {
  const trimmed = item.trim();

  if (!trimmed) return [];
  if (trimmed.startsWith('#pragma')) return [trimmed];
  if (trimmed.includes('(')) return [trimmed];
  if (/^(struct|union|enum)\s+/.test(trimmed)) return [trimmed];

  return trimmed
    .split(/\s+/)
    .map(sanitizeToken)
    .filter(Boolean);
};

const getFunctionName = (signature) => {
  const matches = [...signature.matchAll(/([A-Za-z_][A-Za-z0-9_]*)\s*\(/g)];
  return matches.length ? matches[matches.length - 1][1] : signature;
};

const getDefinitionName = (signature) => {
  const trimmed = signature.trim();

  if (trimmed.startsWith('#define ')) {
    return trimmed.replace('#define ', '').split(/\s|\(/)[0];
  }

  if (/^(struct|union|enum)\s+/.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.includes('(')) {
    return trimmed.slice(0, trimmed.indexOf('(')).trim();
  }

  return trimmed;
};

const describePragma = (pragma) => {
  if (pragma.includes('CX_LIMITED_RANGE')) {
    return meaningUsage(
      '複素数演算の評価方法に関するコンパイラ指示です。',
      '複素演算の精度と最適化の扱いを切り替えたいときに、ソース中で on / off を指定して使います。'
    );
  }

  if (pragma.includes('FENV_ACCESS')) {
    return meaningUsage(
      '浮動小数点環境をプログラムが明示的に参照することを伝えるコンパイラ指示です。',
      '丸めモードや例外フラグを正しく反映させたい数値コードで on / off を指定して使います。'
    );
  }

  if (pragma.includes('FP_CONTRACT')) {
    return meaningUsage(
      '浮動小数点式の縮約最適化を許可するかを示すコンパイラ指示です。',
      'FMA などの縮約を許すか禁止するかを数値計算の精度要件に応じて切り替えて使います。'
    );
  }

  return meaningUsage(
    'コンパイラへ追加の処理方針を伝える指示です。',
    '対象ブロックの最適化や評価規則を制御したい場面で、規格が定める形式に従って使います。'
  );
};

const describeFeatureMacro = (token) => {
  if (token === '__STDC_WANT_LIB_EXT1__') {
    return meaningUsage(
      'Annex K の bounds-checking interfaces を有効にしたいことを示す機能テストマクロです。',
      '`#include` より前に定義しておき、`_s` 系 API を宣言させたいときに使います。'
    );
  }

  if (token === '__STDC_NO_COMPLEX__') {
    return meaningUsage(
      '実装が複素数機能を提供しないことを示すマクロです。',
      '移植コードで `<complex.h>` の利用可否を条件分岐したいときに参照します。'
    );
  }

  return meaningUsage(
    '実装やライブラリ機能の有無を示す機能テストマクロです。',
    '条件付きコンパイルで利用可否を判定したいときに `#if` で参照します。'
  );
};

const describeDefinitionByPattern = (header, token) => {
  if (exactDefinitionDescriptions[token]) {
    return exactDefinitionDescriptions[token];
  }

  if (token.startsWith('__STDC_')) {
    return describeFeatureMacro(token);
  }

  if (/^[A-Za-z_][A-Za-z0-9_]*_t$/.test(token)) {
    return meaningUsage(
      `\`${token}\` は ${header} で使う補助型です。`,
      '対応する関数の引数・戻り値・一時変数の型として宣言し、ライブラリが想定する表現で値を扱うときに使います。'
    );
  }

  if (/^LC_/.test(token)) {
    return meaningUsage(
      'ロケールカテゴリを指定する定数です。',
      '`setlocale` の第 1 引数へ渡し、どのカテゴリの設定を切り替えるか指定するときに使います。'
    );
  }

  if (/^SEEK_/.test(token)) {
    return meaningUsage(
      'ファイル位置移動の基準点を表す定数です。',
      '`fseek` の第 3 引数へ渡し、先頭・現在位置・末尾のどこを基準にするか指定するときに使います。'
    );
  }

  if (/^EXIT_/.test(token)) {
    return meaningUsage(
      'プログラム終了状態を表す定数です。',
      '`exit` や `return` の終了コードとして渡し、成功か失敗かを呼び出し元へ伝えるときに使います。'
    );
  }

  if (/^RAND_MAX$/.test(token)) {
    return meaningUsage(
      '`rand` が返し得る最大値を表す定数です。',
      '乱数を正規化したいときに、`rand() / (double)RAND_MAX` のような形で使います。'
    );
  }

  if (/^(EDOM|EILSEQ|ERANGE)$/.test(token)) {
    return meaningUsage(
      'ライブラリエラーの種類を表す定数です。',
      '`errno` と照合して、失敗理由ごとに分岐や診断メッセージを変えたいときに使います。'
    );
  }

  if (/^FE_/.test(token)) {
    return meaningUsage(
      '浮動小数点例外や丸めモードを表す定数です。',
      '`feclearexcept` や `fesetround` に渡し、どの例外やモードを操作するか指定するときに使います。'
    );
  }

  if (/^FP_/.test(token)) {
    return meaningUsage(
      '浮動小数点値の分類や実装特性を表す定数です。',
      '`fpclassify` の結果比較や、数値計算の最適化可能性の判定に使います。'
    );
  }

  if (/^(HUGE_VAL|HUGE_VALF|HUGE_VALL|INFINITY|NAN)$/.test(token)) {
    return meaningUsage(
      '特殊な浮動小数点値を表す定数です。',
      'オーバーフロー表現や無限大、NaN を扱いたいときに比較や初期化へ使います。'
    );
  }

  if (/^(MATH_ERRNO|MATH_ERREXCEPT|math_errhandling)$/.test(token)) {
    return meaningUsage(
      '数学関数がエラーをどの方法で報告するかを示す識別子です。',
      '数値関数呼び出し後に `errno` や例外フラグを確認すべきか判断するときに使います。'
    );
  }

  if (/^(CHAR_BIT|MB_LEN_MAX|.*_(MIN|MAX)|.*_EPSILON|.*_DIG|.*_EXP|FLT_RADIX|FLT_ROUNDS|FLT_EVAL_METHOD|.*_SUBNORM|DECIMAL_DIG|RSIZE_MAX|SIZE_MAX|WCHAR_(MIN|MAX)|WINT_(MIN|MAX)|PTRDIFF_(MIN|MAX)|SIG_ATOMIC_(MIN|MAX))$/.test(token)) {
    return meaningUsage(
      '型の範囲や精度、実装上の上限を表す定数です。',
      '配列サイズの見積もり、オーバーフロー回避、型の限界値との比較に使います。'
    );
  }

  if (/^(PRI|SCN)/.test(token)) {
    return meaningUsage(
      '固定幅整数型向けの書式指定マクロです。',
      '`printf` や `scanf` の書式文字列へ連結し、`int32_t` などを移植性を保って入出力するときに使います。'
    );
  }

  if (/^(INT|UINT).+_C$/.test(token)) {
    return meaningUsage(
      '固定幅整数リテラルを適切な型で生成するためのマクロです。',
      '`INT32_C(10)` のように書いて、定数式の型を規格どおり明示したいときに使います。'
    );
  }

  if (/^ATOMIC_/.test(token)) {
    return meaningUsage(
      'アトミック機能の能力値や初期化子を表す識別子です。',
      '対象型が lock-free かどうかを確認したり、アトミックオブジェクトを初期化したりするときに使います。'
    );
  }

  if (/^memory_order_/.test(token)) {
    return meaningUsage(
      'アトミック操作のメモリ順序を表す列挙値です。',
      '`atomic_store_explicit` などの explicit 版に渡し、可視化順序の強さを選ぶときに使います。'
    );
  }

  if (/^(and|and_eq|bitand|bitor|compl|not|not_eq|or|or_eq|xor|xor_eq)$/.test(token)) {
    return meaningUsage(
      '演算子の代替表記トークンです。',
      'ソースコードで `&&` や `|` の代わりに読みやすい表記を使いたいときに使います。'
    );
  }

  if (/^(SIG_DFL|SIG_ERR|SIG_IGN|SIGABRT|SIGFPE|SIGILL|SIGINT|SIGSEGV|SIGTERM)$/.test(token)) {
    return meaningUsage(
      'シグナルの既定動作やシグナル番号を表す定数です。',
      '`signal` や `raise` と組み合わせて、どのシグナルを扱うか指定するときに使います。'
    );
  }

  if (/^(thread_local|mtx_plain|mtx_recursive|mtx_timed|thrd_timedout|thrd_success|thrd_busy|thrd_error|thrd_nomem|TIME_UTC|TSS_DTOR_ITERATIONS)$/.test(token)) {
    return meaningUsage(
      'スレッド API の動作種別や結果コードを表す識別子です。',
      'スレッド生成、ミューテックス初期化、待機結果の判定などでモードや戻り値比較に使います。'
    );
  }

  if (/^WEOF$/.test(token)) {
    return meaningUsage(
      'ワイド文字入出力で EOF やエラーを表す特殊値です。',
      '`getwc` などの戻り値と比較し、入力終了かどうかを判定するときに使います。'
    );
  }

  if (/^(FP_FAST_FMA|FP_FAST_FMAF|FP_FAST_FMAL)$/.test(token)) {
    return meaningUsage(
      'FMA が高速に実装されている可能性を示すマクロです。',
      '数値計算で `fma` を優先利用してもコストが高くないか判断したいときに使います。'
    );
  }

  if (/^L_tmpnam(_s)?$|^TMP_MAX(_S)?$|^FOPEN_MAX$|^FILENAME_MAX$|^BUFSIZ$|^_IO(F|L|N)BF$/.test(token)) {
    return meaningUsage(
      '標準入出力ライブラリの制約値やバッファリング方式を表す定数です。',
      '一時ファイル名バッファの確保や `setvbuf` のモード指定などで使います。'
    );
  }

  if (/^(complex|imaginary|_Complex_I|_Imaginary_I|I)$/.test(token)) {
    return meaningUsage(
      '複素数や虚数を扱うための表記・定数です。',
      '複素数型の宣言や、虚数単位を含む式を組み立てるときに使います。'
    );
  }

  return meaningUsage(
    `\`${token}\` は ${header} に属する識別子です。`,
    '対応する関数の引数や戻り値、条件分岐、初期化式の中で、ライブラリが求める意味を表すために使います。'
  );
};

const describeMathFunction = (name) => {
  const { baseName, variant } = normalizeFunctionName(name, mathVariantBases);
  const meaningCore = mathBaseMeanings[baseName];

  if (!meaningCore) {
    return null;
  }

  const variantText =
    variant === 'float'
      ? 'float 版です。'
      : variant === 'long double'
        ? 'long double 版です。'
        : '';

  return meaningUsage(
    `\`${name}\` は ${meaningCore}${variantText}`,
    '計算したい実数値を引数へ渡し、戻り値として計算結果を受け取る形で使います。'
  );
};

const describeComplexFunction = (name) => {
  const { baseName, variant } = normalizeFunctionName(name, complexVariantBases);
  const meaningCore = complexBaseMeanings[baseName];

  if (!meaningCore) {
    return null;
  }

  const variantText =
    variant === 'float'
      ? 'float complex 版です。'
      : variant === 'long double'
        ? 'long double complex 版です。'
        : '';

  return meaningUsage(
    `\`${name}\` は ${meaningCore}${variantText}`,
    '複素数値を引数へ渡し、戻り値から計算結果や成分を取り出す形で使います。'
  );
};

const describeFunctionByPattern = (header, name) => {
  if (exactFunctionDescriptions[name]) {
    return exactFunctionDescriptions[name];
  }

  const complexDescription = describeComplexFunction(name);
  if (header === '<complex.h>' && complexDescription) return complexDescription;

  const mathDescription = describeMathFunction(name);
  if ((header === '<math.h>' || header === '<tgmath.h>') && mathDescription) return mathDescription;

  if (/printf/.test(name)) {
    const target =
      name.includes('snprintf') || name.includes('sprintf') || name.includes('swprintf') || name.includes('vswprintf')
        ? '文字列バッファ'
        : name.includes('fprintf') || name.includes('fwprintf') || name.includes('vfprintf') || name.includes('vfwprintf')
          ? '指定したストリーム'
          : '標準出力';

    return meaningUsage(
      `\`${name}\` は書式文字列に従って ${target} へ値を書き出す関数です。`,
      '第 1 引数の書式に値を対応付けて呼び出します。成功時の戻り値は出力した文字数で、失敗時は負の値になるため、必要なら戻り値で結果を確認して使います。'
    );
  }

  if (/scanf/.test(name)) {
    const source =
      name.includes('sscanf') || name.includes('swscanf') || name.includes('vswscanf')
        ? '入力文字列'
        : name.includes('fscanf') || name.includes('fwscanf') || name.includes('vfscanf') || name.includes('vfwscanf')
          ? '指定したストリーム'
          : '標準入力';

    return meaningUsage(
      `\`${name}\` は書式文字列に従って ${source} を解析し、値を取り込む関数です。`,
      '書き込み先変数のアドレスを渡して呼び出します。戻り値は代入に成功した項目数で、最初の変換前に入力不一致なら 0、入力失敗や EOF なら `EOF` を返すため、それを確認しながら使います。'
    );
  }

  if (/^(fopen|freopen|fopen_s|freopen_s)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はファイルを開いてストリームを初期化する関数です。`,
      'パスとモードを渡してファイル入出力を始めたいときに使い、使い終わったら `fclose` で閉じます。'
    );
  }

  if (/^(fclose|fflush|remove|rename|tmpfile|tmpnam|tmpfile_s|tmpnam_s)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はファイルや一時ファイル名の管理に使う関数です。`,
      'ストリームを閉じる、バッファを吐き出す、ファイル名を操作するなどの管理処理で使います。'
    );
  }

  if (/^(setbuf|setvbuf)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はストリームのバッファリング方式を設定する関数です。`,
      '大きな出力の性能調整や無バッファ化が必要な場面で、対象ストリームへ設定を適用して使います。'
    );
  }

  if (/^(fgetc|getc|getchar|fgetwc|getwc|getwchar)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は入力から 1 文字ずつ読み取る関数です。`,
      '戻り値を文字コードとして受け取り、EOF との比較で入力終了を判定しながら使います。'
    );
  }

  if (/^(fgets|fgetws)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は 1 行または指定長ぶんの文字列を読み取る関数です。`,
      'バッファと最大長を渡して安全に文字列を取り込みたいときに使います。'
    );
  }

  if (/^(fputc|putc|putchar|fputwc|putwc|putwchar)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は 1 文字ずつ出力する関数です。`,
      '文字コードやワイド文字を 1 つ渡して、逐次的に出力を組み立てたいときに使います。'
    );
  }

  if (/^(fputs|puts|fputws)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は文字列をそのまま出力する関数です。`,
      '終端 `\\0` を持つ文字列をストリームや標準出力へ書き出したいときに使います。'
    );
  }

  if (/^(ungetc|ungetwc)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は読み過ぎた 1 文字を入力ストリームへ押し戻す関数です。`,
      '先読みした文字を後続処理で再利用したいときに使います。'
    );
  }

  if (/^(fread|fwrite)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は配列や構造体をバイト列としてまとめて読み書きする関数です。`,
      '要素サイズと個数を指定してブロック単位のファイル入出力を行いたいときに使います。'
    );
  }

  if (/^(fgetpos|fseek|fsetpos|ftell|rewind)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はファイルの現在位置を取得または移動する関数です。`,
      'ランダムアクセスや再読込が必要なファイル処理で、読み書き位置を制御するときに使います。'
    );
  }

  if (/^(clearerr|feof|ferror|perror)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はストリームの状態確認やエラー報告に使う関数です。`,
      '読み書き失敗後に EOF かエラーかを判定したり、原因を表示したりするときに使います。'
    );
  }

  if (/^(malloc|calloc|realloc|aligned_alloc|free)$/.test(name)) {
    if (name === 'free') {
      return meaningUsage(
        `\`${name}\` は動的確保したメモリを解放する関数です。`,
        '対応する確保関数で得たポインタを 1 回だけ渡して解放します。解放後のポインタは再利用せず、必要なら `NULL` を代入して無効化します。'
      );
    }

    return meaningUsage(
      `\`${name}\` は動的メモリを確保する関数です。`,
      '実行時に必要量が決まるバッファやデータ構造をヒープ上で管理したいときに使います。C では戻り値の `void *` は目的のポインタ型へ暗黙変換できるので通常はキャスト不要ですが、C++ では明示的なキャストが必要です。'
    );
  }

  if (/^(atof|atoi|atol|atoll|strtod|strtof|strtold|strtol|strtoll|strtoul|strtoull|wcstod|wcstof|wcstold|wcstol|wcstoll|wcstoul|wcstoull|strtoimax|strtoumax|wcstoimax|wcstoumax)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は文字列を数値へ変換する関数です。`,
      '設定ファイルや入力文字列を数値として解釈したいときに使い、必要なら終端位置やエラー状態も確認します。'
    );
  }

  if (/^(rand|srand)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は擬似乱数を扱う関数です。`,
      '`srand` で種を設定してから `rand` を呼び、乱数系列を生成するときに使います。'
    );
  }

  if (/^(abort|exit|_Exit|quick_exit|atexit|at_quick_exit)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はプログラム終了や終了時処理の登録を行う関数です。`,
      '正常終了・異常終了の方針を決めたり、終了前の後始末関数を登録したりするときに使います。'
    );
  }

  if (/^(getenv|getenv_s|system)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は環境変数や外部コマンド実行に関わる関数です。`,
      '実行環境の設定を読む、または OS 側へ処理を委ねたいときに使います。'
    );
  }

  if (/^(bsearch|bsearch_s|qsort|qsort_s)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は比較関数を使って配列を探索または整列する関数です。`,
      '任意の型配列へ汎用アルゴリズムを適用したいときに、要素サイズと比較関数を渡して使います。'
    );
  }

  if (/^(abs|labs|llabs|imaxabs|div|ldiv|lldiv|imaxdiv)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は整数の絶対値や整数除算結果を求める関数です。`,
      '商と余りを同時に得たい場面や、符号付き整数の絶対値が必要な場面で使います。'
    );
  }

  if (/^(mblen|mbtowc|wctomb|mbstowcs|wcstombs|mbrlen|mbrtowc|wcrtomb|mbsrtowcs|wcsrtombs|mbrtoc16|c16rtomb|mbrtoc32|c32rtomb|wctomb_s|mbstowcs_s|wcstombs_s|wcrtomb_s|mbsrtowcs_s|wcsrtombs_s)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は多バイト文字列とワイド文字・Unicode コード単位の相互変換を行う関数です。`,
      '文字エンコーディング境界をまたぐ入出力や内部表現の変換が必要なときに使います。'
    );
  }

  if (/^(memcpy|memmove|memcpy_s|memmove_s|wmemcpy|wmemmove|wmemcpy_s|wmemmove_s)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はメモリ領域をコピーまたは移動する関数です。`,
      '配列やバッファをバイト単位またはワイド文字単位で複写したいときに使います。'
    );
  }

  if (/^(strcpy|strncpy|strcat|strncat|strcpy_s|strncpy_s|strcat_s|strncat_s|wcscpy|wcsncpy|wcscat|wcsncat|wcscpy_s|wcsncpy_s|wcscat_s|wcsncat_s)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は文字列をコピーまたは連結する関数です。`,
      '終端文字を持つ文字列を別バッファへ複写したり、末尾へつなげたりするときに使います。'
    );
  }

  if (/^(memcmp|strcmp|strcoll|strncmp|wcscmp|wcscoll|wmemcmp|wcsncmp)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は 2 つの領域や文字列を比較する関数です。`,
      'ソートや等価判定、辞書順比較のために戻り値の正負や 0 を見て使います。'
    );
  }

  if (/^(strxfrm|wcsxfrm)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はロケール依存比較向けに文字列を変換する関数です。`,
      '`strcoll` 相当の順序で比較しやすい中間表現をあらかじめ作りたいときに使います。'
    );
  }

  if (/^(memchr|strchr|strcspn|strpbrk|strrchr|strspn|strstr|strtok|strtok_s|wcschr|wcscspn|wcspbrk|wcsrchr|wcsspn|wcsstr|wcstok|wcstok_s|wmemchr)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は文字列やメモリ領域から位置や区切りを探す関数です。`,
      '検索、トークン分割、文字集合チェックなど、解析処理の一部として使います。'
    );
  }

  if (/^(memset|memset_s|wmemset)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は領域全体を同じ値で埋める関数です。`,
      'バッファ初期化、ゼロクリア、既定値の一括設定などで使います。'
    );
  }

  if (/^(strerror|strerror_s|strerrorlen_s)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はエラー番号に対応する説明文字列を扱う関数です。`,
      '`errno` の内容をユーザー向けメッセージへ変換したいときに使います。'
    );
  }

  if (/^(strlen|strnlen_s|wcslen|wcsnlen_s)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は文字列の長さを数える関数です。`,
      'バッファ長の確認やループ回数の決定など、文字列サイズを知りたいときに使います。'
    );
  }

  if (/^(isalnum|isalpha|isblank|iscntrl|isdigit|isgraph|islower|isprint|ispunct|isspace|isupper|isxdigit|iswalnum|iswalpha|iswblank|iswcntrl|iswdigit|iswgraph|iswlower|iswprint|iswpunct|iswspace|iswupper|iswxdigit|iswctype)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は文字が特定の分類に属するか判定する関数です。`,
      '入力文字の種類に応じて処理を分けたいときに、戻り値が 0 かどうかで判定して使います。'
    );
  }

  if (/^(tolower|toupper|towlower|towupper|towctrans)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は文字の変換を行う関数です。`,
      '大小文字の正規化や指定変換規則の適用が必要なときに使います。'
    );
  }

  if (/^(wctype|wctrans)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はワイド文字の分類規則や変換規則を名前から取得する関数です。`,
      '文字種や変換種別を文字列指定で動的に選びたいときに使います。'
    );
  }

  if (/^fe/.test(name)) {
    return meaningUsage(
      `\`${name}\` は浮動小数点例外や丸め環境を取得・設定する関数です。`,
      '数値計算の前後で例外フラグや丸めモードを制御・検査したいときに使います。'
    );
  }

  if (/^(fpclassify|isfinite|isinf|isnan|isnormal|signbit|isgreater|isgreaterequal|isless|islessequal|islessgreater|isunordered)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は浮動小数点値の分類や比較を行うマクロです。`,
      'NaN や無限大を含む実数比較を安全に記述したいときに使います。'
    );
  }

  if (/^atomic_/.test(name) || name === 'kill_dependency') {
    return meaningUsage(
      `\`${name}\` はアトミック操作やメモリ順序制御を行う関数・総称マクロです。`,
      '複数スレッドから共有データを競合なく更新したいときに、必要なメモリ順序を指定して使います。'
    );
  }

  if (/^(cnd_|mtx_|thrd_|tss_)/.test(name)) {
    return meaningUsage(
      `\`${name}\` は C11 スレッド API の同期やスレッド管理に使う関数です。`,
      'スレッド生成、待機、ロック、条件通知、スレッド固有データ管理などで使います。'
    );
  }

  if (/^(fwprintf|fwscanf|swprintf|swscanf|vfwprintf|vfwscanf|vswprintf|vswscanf|vwprintf|vwscanf|wprintf|wscanf|fwprintf_s|fwscanf_s|snwprintf_s|swprintf_s|swscanf_s|vfwprintf_s|vfwscanf_s|vsnwprintf_s|vswprintf_s|vswscanf_s|vwprintf_s|vwscanf_s|wprintf_s|wscanf_s)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はワイド文字列向けの整形入出力を行う関数です。`,
      'ワイド文字の書式文字列を使って、ストリームやバッファへ入出力するときに使います。'
    );
  }

  if (/^(fwide|btowc|wctob)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` はストリームのワイド文字方向や文字コード変換を扱う関数です。`,
      'ナロー文字とワイド文字の境界をまたぐ入出力で向きや変換を調整したいときに使います。'
    );
  }

  if (/^(c16rtomb|c32rtomb|mbrtoc16|mbrtoc32)$/.test(name)) {
    return meaningUsage(
      `\`${name}\` は UTF-16 / UTF-32 コード単位と多バイト列の変換を行う関数です。`,
      'Unicode コード単位ベースのデータと外部バイト列を相互変換したいときに使います。'
    );
  }

  return meaningUsage(
    `\`${name}\` は ${header} に属するライブラリ関数またはマクロです。`,
    '宣言に示された引数と戻り値の型に従って呼び出し、対象ヘッダが担当する処理を行うときに使います。'
  );
};

const exactFunctionExamples = {
  assert: `#include <assert.h>\n\nint value = 10;\nassert(value > 0);`,
  setlocale: `#include <locale.h>\n\nsetlocale(LC_ALL, \"ja_JP.UTF-8\");`,
  localeconv: `#include <locale.h>\n\nstruct lconv *info = localeconv();\nprintf(\"%s\\n\", info->decimal_point);`,
  setjmp: `#include <setjmp.h>\n\njmp_buf env;\nif (setjmp(env) == 0) {\n    /* ここで処理 */\n}`,
  longjmp: `#include <setjmp.h>\n\njmp_buf env;\nlongjmp(env, 1);`,
  signal: `#include <signal.h>\n\nsignal(SIGINT, SIG_IGN);`,
  raise: `#include <signal.h>\n\nraise(SIGTERM);`,
  va_start: `va_list ap;\nva_start(ap, count);`,
  va_arg: `int value = va_arg(ap, int);`,
  va_copy: `va_list copy;\nva_copy(copy, ap);`,
  va_end: `va_end(ap);`,
  offsetof: `#include <stddef.h>\n\nsize_t offset = offsetof(struct Sample, member);`,
  call_once: `once_flag flag = ONCE_FLAG_INIT;\ncall_once(&flag, init_resource);`,
  thrd_current: `thrd_t self = thrd_current();`,
  thrd_yield: `thrd_yield();`,
  time: `time_t now = time(NULL);`,
  clock: `clock_t start = clock();`,
  difftime: `double sec = difftime(end_time, start_time);`,
  mktime: `time_t ts = mktime(&tm_value);`,
  timespec_get: `struct timespec ts;\ntimespec_get(&ts, TIME_UTC);`,
  asctime: `char *text = asctime(&tm_value);`,
  ctime: `char *text = ctime(&now);`,
  gmtime: `struct tm *utc = gmtime(&now);`,
  localtime: `struct tm *local = localtime(&now);`,
  strftime: `char buf[64];\nstrftime(buf, sizeof(buf), \"%Y-%m-%d\", &tm_value);`
};

const getMathExample = (name) => {
  const { baseName, variant } = normalizeFunctionName(name, mathVariantBases);
  const literal =
    variant === 'float' ? '0.5f'
    : variant === 'long double' ? '0.5L'
    : '0.5';
  const typeName =
    variant === 'float' ? 'float'
    : variant === 'long double' ? 'long double'
    : 'double';

  if (['pow'].includes(baseName)) {
    return `${typeName} value = ${name}(${literal}, ${variant === 'float' ? '3.0f' : variant === 'long double' ? '3.0L' : '3.0'});`;
  }

  if (['atan2', 'hypot', 'fdim', 'fmax', 'fmin', 'copysign', 'nextafter', 'nexttoward', 'remainder', 'fmod'].includes(baseName)) {
    const second =
      variant === 'float' ? '1.0f'
      : variant === 'long double' ? '1.0L'
      : '1.0';
    return `${typeName} value = ${name}(${literal}, ${second});`;
  }

  if (baseName === 'fma') {
    const a = literal;
    const b = variant === 'float' ? '2.0f' : variant === 'long double' ? '2.0L' : '2.0';
    const c = variant === 'float' ? '1.0f' : variant === 'long double' ? '1.0L' : '1.0';
    return `${typeName} value = ${name}(${a}, ${b}, ${c});`;
  }

  if (baseName === 'frexp') {
    return `int exp_part;\n${typeName} frac = ${name}(${literal}, &exp_part);`;
  }

  if (baseName === 'ldexp') {
    return `${typeName} value = ${name}(${literal}, 4);`;
  }

  if (baseName === 'modf') {
    return `${typeName} int_part;\n${typeName} frac = ${name}(${literal}, &int_part);`;
  }

  if (baseName === 'remquo') {
    return `int quo;\n${typeName} value = ${name}(${literal}, ${variant === 'float' ? '0.2f' : variant === 'long double' ? '0.2L' : '0.2'}, &quo);`;
  }

  if (baseName === 'nan') {
    return `${typeName} value = ${name}(\"\");`;
  }

  return `${typeName} value = ${name}(${literal});`;
};

const getComplexExample = (name) => {
  const { variant } = normalizeFunctionName(name, complexVariantBases);
  const typeName =
    variant === 'float' ? 'float complex'
    : variant === 'long double' ? 'long double complex'
    : 'double complex';
  const scalarType =
    variant === 'float' ? 'float'
    : variant === 'long double' ? 'long double'
    : 'double';
  const realOne =
    variant === 'float' ? '1.0f'
    : variant === 'long double' ? '1.0L'
    : '1.0';
  const imagTwo =
    variant === 'float' ? '2.0f'
    : variant === 'long double' ? '2.0L'
    : '2.0';

  if (/^CMPLX/.test(name)) {
    return `${typeName} z = ${name}(${realOne}, ${imagTwo});`;
  }

  if (/^(cimag|creal|carg|cabs)/.test(name)) {
    return `${typeName} z = ${realOne} + ${imagTwo} * I;\n${scalarType} value = ${name}(z);`;
  }

  if (/^cpow/.test(name)) {
    return `${typeName} x = ${realOne} + ${imagTwo} * I;\n${typeName} y = ${realOne};\n${typeName} z = ${name}(x, y);`;
  }

  return `${typeName} z = ${realOne} + ${imagTwo} * I;\n${typeName} result = ${name}(z);`;
};

const describeExampleByPattern = (header, name) => {
  if (exactFunctionExamples[name]) {
    return exactFunctionExamples[name];
  }

  if (header === '<complex.h>') {
    const example = getComplexExample(name);
    if (example) return example;
  }

  if (header === '<math.h>' || header === '<tgmath.h>') {
    const example = getMathExample(name);
    if (example) return example;
  }

  if (/printf/.test(name)) {
    return `int written = printf(\"value = %d\\n\", value);`;
  }

  if (/scanf/.test(name)) {
    return `int value;\nint read = scanf(\"%d\", &value);`;
  }

  if (/^(fopen|freopen)/.test(name)) {
    return `FILE *fp = fopen(\"data.txt\", \"r\");`;
  }

  if (/^(fclose|fflush)$/.test(name)) {
    return `${name}(fp);`;
  }

  if (/^(remove|rename)$/.test(name)) {
    return name === 'remove'
      ? `remove(\"old.txt\");`
      : `rename(\"old.txt\", \"new.txt\");`;
  }

  if (/^(tmpfile|tmpfile_s)$/.test(name)) {
    return name === 'tmpfile'
      ? `FILE *fp = tmpfile();`
      : `FILE *fp;\ntmpfile_s(&fp);`;
  }

  if (/^(tmpnam|tmpnam_s)$/.test(name)) {
    return name === 'tmpnam'
      ? `char name[L_tmpnam];\ntmpnam(name);`
      : `char name[L_tmpnam_s];\ntmpnam_s(name, L_tmpnam_s);`;
  }

  if (/^(setbuf|setvbuf)$/.test(name)) {
    return name === 'setbuf'
      ? `char buf[BUFSIZ];\nsetbuf(fp, buf);`
      : `char buf[BUFSIZ];\nsetvbuf(fp, buf, _IOFBF, BUFSIZ);`;
  }

  if (/^(fgetc|getc|getchar)$/.test(name)) {
    return `int ch = ${name === 'getchar' ? 'getchar()' : `${name}(fp)`};`;
  }

  if (/^(fgets)$/.test(name)) {
    return `char line[128];\nfgets(line, sizeof(line), fp);`;
  }

  if (/^(fputc|putc|putchar)$/.test(name)) {
    return name === 'putchar'
      ? `putchar('A');`
      : `${name}('A', fp);`;
  }

  if (/^(fputs|puts)$/.test(name)) {
    return name === 'puts'
      ? `puts(\"hello\");`
      : `fputs(\"hello\\n\", fp);`;
  }

  if (/^(ungetc)$/.test(name)) {
    return `ungetc('x', fp);`;
  }

  if (/^(fread|fwrite)$/.test(name)) {
    return `${name}(buffer, sizeof(int), count, fp);`;
  }

  if (/^(fgetpos|fsetpos)$/.test(name)) {
    return name === 'fgetpos'
      ? `fpos_t pos;\nfgetpos(fp, &pos);`
      : `fsetpos(fp, &pos);`;
  }

  if (/^(fseek|ftell|rewind)$/.test(name)) {
    if (name === 'fseek') return `fseek(fp, 0, SEEK_SET);`;
    if (name === 'ftell') return `long pos = ftell(fp);`;
    return `rewind(fp);`;
  }

  if (/^(clearerr|feof|ferror|perror)$/.test(name)) {
    if (name === 'clearerr') return `clearerr(fp);`;
    if (name === 'perror') return `perror(\"open failed\");`;
    return `if (${name}(fp)) {\n    /* 状態を確認 */\n}`;
  }

  if (/^(malloc|calloc|realloc|aligned_alloc|free)$/.test(name)) {
    if (name === 'malloc') return `int *ptr = malloc(10 * sizeof(int)); /* C ではキャスト不要 */`;
    if (name === 'calloc') return `int *ptr = calloc(10, sizeof(int)); /* C ではキャスト不要 */`;
    if (name === 'realloc') return `ptr = realloc(ptr, 20 * sizeof(int));`;
    if (name === 'aligned_alloc') return `void *ptr = aligned_alloc(32, 128);`;
    return `free(ptr);`;
  }

  if (/^(atof|atoi|atol|atoll)$/.test(name)) {
    return `int value = ${name}(text);`;
  }

  if (/^(strtod|strtof|strtold|strtol|strtoll|strtoul|strtoull|strtoimax|strtoumax)$/.test(name)) {
    return `char *end;\nlong value = ${name}(text, &end, 10);`;
  }

  if (/^(wcstod|wcstof|wcstold|wcstol|wcstoll|wcstoul|wcstoull|wcstoimax|wcstoumax)$/.test(name)) {
    return `wchar_t *end;\nlong value = ${name}(text, &end, 10);`;
  }

  if (/^(rand|srand)$/.test(name)) {
    return name === 'srand'
      ? `srand((unsigned)time(NULL));`
      : `int value = rand();`;
  }

  if (/^(abort|exit|_Exit|quick_exit)$/.test(name)) {
    return `${name}(EXIT_FAILURE);`;
  }

  if (/^(atexit|at_quick_exit)$/.test(name)) {
    return `${name}(cleanup);`;
  }

  if (/^(getenv|getenv_s)$/.test(name)) {
    return name === 'getenv'
      ? `char *home = getenv(\"HOME\");`
      : `size_t len;\ngetenv_s(&len, buffer, sizeof(buffer), \"HOME\");`;
  }

  if (/^(system)$/.test(name)) {
    return `system(\"dir\");`;
  }

  if (/^(bsearch|bsearch_s)$/.test(name)) {
    return `int *found = bsearch(&key, values, count, sizeof(int), compare_int);`;
  }

  if (/^(qsort|qsort_s)$/.test(name)) {
    return `qsort(values, count, sizeof(int), compare_int);`;
  }

  if (/^(abs|labs|llabs|imaxabs)$/.test(name)) {
    return `int value = ${name}(-42);`;
  }

  if (/^(div|ldiv|lldiv|imaxdiv)$/.test(name)) {
    if (name === 'div') return `div_t result = div(17, 5);`;
    if (name === 'ldiv') return `ldiv_t result = ldiv(17L, 5L);`;
    if (name === 'lldiv') return `lldiv_t result = lldiv(17LL, 5LL);`;
    return `imaxdiv_t result = imaxdiv(17, 5);`;
  }

  if (/^(mblen|mbrlen)$/.test(name)) {
    return `size_t len = ${name}(text, MB_CUR_MAX);`;
  }

  if (/^(mbtowc|mbrtowc)$/.test(name)) {
    return `wchar_t wc;\n${name}(&wc, text, MB_CUR_MAX);`;
  }

  if (/^(wctomb|wcrtomb|wctomb_s|wcrtomb_s)$/.test(name)) {
    return `char buf[MB_CUR_MAX];\n${name}(buf, wc${name.endsWith('_s') ? ', sizeof(buf)' : ''});`;
  }

  if (/^(mbstowcs|wcstombs|mbsrtowcs|wcsrtombs|mbstowcs_s|wcstombs_s|mbsrtowcs_s|wcsrtombs_s)$/.test(name)) {
    return `/* 変換先バッファを用意して ${name} を呼びます */`;
  }

  if (/^(memcpy|memmove|memcpy_s|memmove_s|wmemcpy|wmemmove|wmemcpy_s|wmemmove_s)$/.test(name)) {
    return `${name}(dest, src, count);`;
  }

  if (/^(strcpy|strncpy|strcat|strncat|strcpy_s|strncpy_s|strcat_s|strncat_s|wcscpy|wcsncpy|wcscat|wcsncat|wcscpy_s|wcsncpy_s|wcscat_s|wcsncat_s)$/.test(name)) {
    return `${name}(dest, src${/n/.test(name) ? ', count' : ''});`;
  }

  if (/^(memcmp|strcmp|strcoll|strncmp|wcscmp|wcscoll|wmemcmp|wcsncmp)$/.test(name)) {
    return `int diff = ${name}(left, right${/ncmp|memcmp/.test(name) ? ', count' : ''});`;
  }

  if (/^(strxfrm|wcsxfrm)$/.test(name)) {
    return `${name}(dest, src, count);`;
  }

  if (/^(memchr|strchr|strcspn|strpbrk|strrchr|strspn|strstr|strtok|strtok_s|wcschr|wcscspn|wcspbrk|wcsrchr|wcsspn|wcsstr|wcstok|wcstok_s|wmemchr)$/.test(name)) {
    return `/* 対象文字列と検索条件を渡して位置や長さを調べます */`;
  }

  if (/^(memset|memset_s|wmemset)$/.test(name)) {
    return `${name}(buffer, 0, count);`;
  }

  if (/^(strerror|strerror_s|strerrorlen_s)$/.test(name)) {
    return name === 'strerror'
      ? `printf(\"%s\\n\", strerror(errno));`
      : `/* エラー番号に対応する説明文を取得します */`;
  }

  if (/^(strlen|strnlen_s|wcslen|wcsnlen_s)$/.test(name)) {
    return `size_t len = ${name}(text${name.includes('nlen') ? ', max_len' : ''});`;
  }

  if (/^(isalnum|isalpha|isblank|iscntrl|isdigit|isgraph|islower|isprint|ispunct|isspace|isupper|isxdigit|tolower|toupper)$/.test(name)) {
    return `${/to/.test(name) ? 'int ch2' : 'int ok'} = ${name}('A');`;
  }

  if (/^(iswalnum|iswalpha|iswblank|iswcntrl|iswdigit|iswgraph|iswlower|iswprint|iswpunct|iswspace|iswupper|iswxdigit|iswctype|towlower|towupper|towctrans|wctype|wctrans)$/.test(name)) {
    return `/* ワイド文字や規則オブジェクトを使って ${name} を呼びます */`;
  }

  if (/^fe/.test(name)) {
    return `/* 浮動小数点環境を扱う前後で ${name} を呼びます */`;
  }

  if (/^(fpclassify|isfinite|isinf|isnan|isnormal|signbit|isgreater|isgreaterequal|isless|islessequal|islessgreater|isunordered)$/.test(name)) {
    return `if (${name}(value${name.startsWith('isg') || name.startsWith('isl') ? ', other' : ''})) {\n    /* 判定に応じて処理 */\n}`;
  }

  if (/^atomic_/.test(name) || name === 'kill_dependency') {
    return `/* 共有変数に対して ${name} を呼びます */`;
  }

  if (/^(cnd_|mtx_|thrd_|tss_)/.test(name)) {
    return `/* スレッドや同期オブジェクトに対して ${name} を呼びます */`;
  }

  if (/^(fwprintf|fwscanf|swprintf|swscanf|vfwprintf|vfwscanf|vswprintf|vswscanf|vwprintf|vwscanf|wprintf|wscanf|fwprintf_s|fwscanf_s|snwprintf_s|swprintf_s|swscanf_s|vfwprintf_s|vfwscanf_s|vsnwprintf_s|vswprintf_s|vswscanf_s|vwprintf_s|vwscanf_s|wprintf_s|wscanf_s)$/.test(name)) {
    return `wprintf(L\"value = %d\\n\", value);`;
  }

  if (/^(fwide|btowc|wctob)$/.test(name)) {
    return `/* 文字ストリームの向きや単一文字変換に ${name} を使います */`;
  }

  if (/^(c16rtomb|c32rtomb|mbrtoc16|mbrtoc32)$/.test(name)) {
    return `/* UTF-16 / UTF-32 と多バイト列の変換に ${name} を使います */`;
  }

  return `/* ${name} の引数に値を渡して呼び出します */`;
};

const buildReferenceItem = (header, item, kind) => {
  const signature = item.trim();
  const exampleCode =
    kind === 'function'
      ? describeExampleByPattern(header, getFunctionName(signature))
      : '';
  const description =
    kind === 'pragma'
      ? describePragma(signature)
      : kind === 'function'
        ? describeFunctionByPattern(header, getFunctionName(signature))
        : describeDefinitionByPattern(header, getDefinitionName(signature));

  return {
    signature,
    meaning: description.meaning,
    usage: description.usage,
    exampleCode
  };
};

export const normalizeReferenceLibrary = (library) => {
  const metadata = referenceMetadata[library.header];
  const sections = library.sections.map((section) => {
    const kind = inferSectionKind(section);
    const rawItems =
      kind === 'definition'
        ? section.items.flatMap(expandDefinitionItems)
        : section.items.map((item) => item.trim()).filter(Boolean);

    return {
      title: normalizeSectionTitle(kind),
      items: rawItems.map((item) => buildReferenceItem(library.header, item, kind))
    };
  });

  return {
    ...library,
    summary: metadata?.summary || library.summary,
    note: metadata?.note ?? library.note,
    sections
  };
};
